import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "@/lib/ThemeContext";
import LegalFooter from "@/components/LegalFooter";

// Bundle F-4. Every sentence below is backed by code in this repository (file named in the F-4 report).
// Liam updates LAST_REVIEWED when the page is re-checked.
export const LAST_REVIEWED = "2026-11-03";

const SECTIONS = [
  { id: "account", title: "Account security", items: [
    "Passwords are stored as bcrypt hashes; the password itself is never kept.",
    "Two-factor authentication with an authenticator app (TOTP) and one-time recovery codes is available to every account.",
    "Passkeys (Face ID, Touch ID, Windows Hello, hardware keys) can be used to sign in instead of a password.",
    "Repeated failed sign-ins lock the attempt for 15 minutes.",
    "Sensitive owner actions require a fresh password + authenticator step-up, not just a session.",
    "Sign-in sessions use short-lived tokens in HttpOnly, Secure, SameSite cookies; changing your password signs out every other session.",
    "New accounts verify their e-mail address with a short-lived link.",
  ] },
  { id: "isolation", title: "Data isolation", items: [
    "Every record carries its owner's user id and an organisation id; every query is scoped to the signed-in user (or their organisation role).",
    "State-changing requests are only accepted from MMNTM's own origin (CSRF / Origin enforcement) in addition to the cookie checks.",
  ] },
  { id: "protection", title: "Data protection", items: [
    "In transit: HTTPS only — the app refuses to start with a non-HTTPS public URL, marks all cookies Secure and sends a Strict-Transport-Security header (one year, including subdomains) on every HTTPS response.",
    "At rest: authenticator (TOTP) secrets are encrypted with a key derived from the server secret; passwords, recovery codes, reset and verification tokens are stored only as hashes; passkeys store the public key only.",
    "Not encrypted by the application at rest: your tasks, notes, sessions and other content are stored in plain form in the database and rely on the database provider's disk encryption.",
  ] },
  { id: "your-data", title: "Your data", items: [
    "Export: Settings → Data & Export gives you a zip with everything we hold for you (JSON, Markdown notes, CSV tasks) — one export every 10 minutes, link valid 24 hours.",
    "Delete: you can delete your account in Settings → Danger zone, or without signing in from the deletion page. Deleted accounts are kept for 30 days so you can undo, then erased.",
    "Security-relevant actions (sign-ins, 2FA and passkey changes, exports, deletions, owner actions) are written to an audit log kept for 400 days.",
  ] },
  { id: "monitoring", title: "Monitoring and logging", items: [
    "Application errors are reported to Sentry with personal data disabled: IP addresses, cookies and request bodies are not sent.",
    "Server logs and audit rows are written without secrets, codes or one-time links; audit rows record masked e-mail addresses only.",
  ] },
  { id: "hosting", title: "Hosting", items: [
    "Database: managed MongoDB (provider to be confirmed). Region: to be confirmed.",
    "Application: Emergent (app hosting). Region: to be confirmed.",
    "E-mail: Resend. Error monitoring: Sentry. Product analytics: PostHog (US Cloud). Payments: Stripe.",
  ] },
];

export default function SecurityPage() {
  const { tokens: t } = useTheme();
  useEffect(() => { document.title = "Security • MMNTM"; return () => { document.title = "MMNTM"; }; }, []);
  const h2 = { color: "var(--tm-text-primary)", fontSize: "20px", letterSpacing: "-0.01em" };
  const body = { color: "var(--tm-text-secondary)", lineHeight: 1.7 };
  const link = { color: "var(--tm-accent)", textDecoration: "underline" };
  return (
    <div data-testid="security-page" style={{ background: "var(--tm-bg-primary, " + t.bgPrimary + ")", minHeight: "calc(100vh - var(--topbar-height, 0px))" }}>
      <article className="mx-auto px-6 sm:px-8 py-10 sm:py-12" style={{ maxWidth: "800px" }}>
        <h1 className="font-bold mb-2" style={{ color: "var(--tm-text-primary)", fontSize: "36px", letterSpacing: "-0.02em", lineHeight: 1.1 }} data-testid="security-title">Security at MMNTM</h1>
        <div className="mb-10 pb-6 text-sm" style={{ ...body, borderBottom: `1px solid var(--tm-border-color, ${t.borderColor})` }} data-testid="security-meta">
          <div><strong>Last reviewed:</strong> <span data-testid="security-last-reviewed">{LAST_REVIEWED}</span></div>
          <div>This page lists what is actually in place. We don't hold SOC 2, ISO 27001 or third-party penetration-test certifications, and we won't claim them here until we do.</div>
        </div>
        {SECTIONS.map((s) => (
          <section key={s.id} id={s.id} className="mb-8" data-testid={`security-section-${s.id}`}>
            <h2 className="font-bold mb-3" style={h2}>{s.title}</h2>
            <ul className="list-disc pl-5 space-y-1.5 text-sm" style={body}>{s.items.map((i) => <li key={i}>{i}</li>)}</ul>
          </section>
        ))}
        <section id="reporting" className="mb-8" data-testid="security-section-reporting">
          <h2 className="font-bold mb-3" style={h2}>Reporting a vulnerability</h2>
          <div className="text-sm space-y-2" style={body}>
            <p>If you believe you've found a security issue, e-mail <a href="mailto:security@getmmntm.app" style={link} data-testid="security-contact">security@getmmntm.app</a> with the steps to reproduce it. We aim to acknowledge reports within 5 business days.</p>
            <p>Good-faith research that avoids accessing other people's data, degrading the service or exfiltrating data will not be pursued legally. The full policy is published as SECURITY.md in our source repository; machine-readable details are at <a href="/.well-known/security.txt" style={link}>/.well-known/security.txt</a>.</p>
            <p>General support: <a href="mailto:support@getmmntm.app" style={link}>support@getmmntm.app</a>.</p>
          </div>
        </section>
        <div className="mt-10 pt-6 text-sm" style={{ borderTop: `1px solid var(--tm-border-color, ${t.borderColor})`, color: "var(--tm-text-secondary)" }}>
          See also our <Link to="/privacy" style={link}>Privacy Policy</Link> and <Link to="/terms" style={link}>Terms of Service</Link>.
        </div>
      </article>
      <LegalFooter />
    </div>
  );
}
